/* MT homepage sections (index template) */
  class MtBanner extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.slides = Array.from(this.querySelectorAll('[data-banner-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-banner-dot]'));
      this.track = this.querySelector('[data-banner-track]');
      this.viewport = this.querySelector('.mt-banner__viewport');
      this.index = 0;
      this.timerId = null;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.interval = Math.max(3000, parseInt(this.dataset.interval, 10) || 6000);
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mobileMq = window.matchMedia('(max-width: 749px)');
      this._touchStartX = 0;
      this._touchStartY = 0;
      this._touchActive = false;
      this._dragOffset = 0;

      this._onViewportChange = () => {
        this._dragOffset = 0;
        this.classList.remove('is-dragging');
        this.classList.toggle('is-mobile', this.isMobile());
        this.syncSlideMetrics();
        this.syncResponsiveMedia();
        this.updateTrack();
      };
      window.addEventListener('resize', this._onViewportChange);
      if (this.mobileMq.addEventListener) {
        this.mobileMq.addEventListener('change', this._onViewportChange);
      } else if (this.mobileMq.addListener) {
        this.mobileMq.addListener(this._onViewportChange);
      }

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.go(parseInt(dot.getAttribute('data-index'), 10) || 0);
          this.restartAutoplay();
        });
      });

      this.prevBtn = this.querySelector('[data-banner-prev]');
      this.nextBtn = this.querySelector('[data-banner-next]');
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.go(this.index - 1);
          this.restartAutoplay();
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.next();
          this.restartAutoplay();
        });
      }

      this.bindMobileSwipe();

      this.addEventListener('mouseenter', this.pause.bind(this));
      this.addEventListener('mouseleave', this.resume.bind(this));
      this.addEventListener('focusin', this.pause.bind(this));
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.resume();
      });

      this._onViewportChange();
      this.startAutoplay();
      this.syncSlideVideos();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this._onViewportChange());
      });

      if (this.viewport && typeof ResizeObserver !== 'undefined') {
        this._ro = new ResizeObserver(() => this._onViewportChange());
        this._ro.observe(this.viewport);
      }
    }

    disconnectedCallback() {
      this.pause();
      this.pauseAllVideos();
      window.removeEventListener('resize', this._onViewportChange);
      if (this._ro) this._ro.disconnect();
      if (this.mobileMq) {
        if (this.mobileMq.removeEventListener) {
          this.mobileMq.removeEventListener('change', this._onViewportChange);
        } else if (this.mobileMq.removeListener) {
          this.mobileMq.removeListener(this._onViewportChange);
        }
      }
    }

    isMobile() {
      return this.mobileMq && this.mobileMq.matches;
    }

    hydrateVideo(video) {
      if (!video || video.dataset.hydrated === 'true') return;
      var hasLazy = false;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        if (!source.getAttribute('src')) {
          source.setAttribute('src', source.getAttribute('data-src'));
          hasLazy = true;
        }
      });
      video.dataset.hydrated = 'true';
      if (hasLazy && video.readyState === 0) video.load();
    }

    pauseAllVideos() {
      this.querySelectorAll('[data-banner-video]').forEach((video) => {
        try {
          video.pause();
        } catch (e) {}
      });
    }

    syncSlideVideos() {
      var isDesktop = !this.isMobile();
      this.slides.forEach((slide, n) => {
        var active = n === this.index;
        var deskWrap = slide.querySelector('.mt-banner__media-desk');
        var mobWrap = slide.querySelector('.mt-banner__media-mob');
        var activeWrap = isDesktop ? deskWrap : mobWrap;
        var inactiveWrap = isDesktop ? mobWrap : deskWrap;

        if (inactiveWrap) {
          inactiveWrap.querySelectorAll('[data-banner-video]').forEach((video) => {
            try {
              video.pause();
            } catch (e) {}
          });
        }

        if (!activeWrap) return;
        var video = activeWrap.querySelector('[data-banner-video]');
        if (!video) return;

        if (active) {
          this.hydrateVideo(video);
          if (!this.reduceMotion) {
            var playPromise = video.play();
            if (playPromise && playPromise.catch) playPromise.catch(function () {});
          }
        } else {
          try {
            video.pause();
          } catch (e) {}
        }
      });
    }

    syncResponsiveMedia() {
      var isDesktop = !this.isMobile();

      this.querySelectorAll('.mt-banner__media').forEach((media) => {
        var deskWrap = media.querySelector('.mt-banner__media-desk');
        var mobWrap = media.querySelector('.mt-banner__media-mob');
        if (!deskWrap || !mobWrap) return;

        deskWrap.hidden = !isDesktop;
        mobWrap.hidden = isDesktop;

        var activeWrap = isDesktop ? deskWrap : mobWrap;
        var img = activeWrap.querySelector('.mt-banner__img');
        if (img) {
          if (img.loading === 'lazy') {
            img.loading = 'eager';
          }

          if (!img.complete && img.src) {
            img.decode().catch(function () {});
          }
        }

        var video = activeWrap.querySelector('[data-banner-video]');
        if (video) this.hydrateVideo(video);
      });

      this.syncSlideVideos();
    }

    getSlideWidth() {
      if (this.viewport) return this.viewport.clientWidth;
      if (this.slides[0]) return this.slides[0].offsetWidth;
      return 0;
    }

    syncSlideMetrics() {
      if (!this.isMobile() || !this.viewport || !this.track || this.slides.length < 2) {
        if (this.track) this.track.style.removeProperty('--bn-slide-w');
        this.slides.forEach((slide) => {
          slide.style.removeProperty('flex-basis');
          slide.style.removeProperty('width');
          slide.style.removeProperty('max-width');
        });
        return;
      }

      var slideW = this.getSlideWidth();
      if (!slideW) return;

      this.track.style.setProperty('--bn-slide-w', slideW + 'px');
      this.slides.forEach((slide) => {
        slide.style.flexBasis = slideW + 'px';
        slide.style.width = slideW + 'px';
        slide.style.maxWidth = slideW + 'px';
      });
    }

    updateTrack() {
      if (!this.track) return;

      if (!this.isMobile() || this.slides.length < 2) {
        this.track.style.transform = '';
        return;
      }

      this.syncSlideMetrics();

      var slideW = this.getSlideWidth();
      if (!slideW) return;

      var x = -(this.index * slideW) + (this._dragOffset || 0);
      this.track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
    }

    bindMobileSwipe() {
      if (this.slides.length < 2) return;

      var onStart = (e) => {
        if (!this.isMobile()) return;
        var touch = e.touches && e.touches[0];
        if (!touch) return;
        this._touchStartX = touch.clientX;
        this._touchStartY = touch.clientY;
        this._touchActive = true;
        this._dragOffset = 0;
        this.pause();
      };

      var onMove = (e) => {
        if (!this._touchActive || !this.isMobile()) return;
        var touch = e.touches[0];
        var dx = touch.clientX - this._touchStartX;
        var dy = touch.clientY - this._touchStartY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
          if (e.cancelable) e.preventDefault();
          this.classList.add('is-dragging');
          var atStart = this.index === 0 && dx > 0;
          var atEnd = this.index === this.slides.length - 1 && dx < 0;
          this._dragOffset = atStart || atEnd ? dx * 0.35 : dx;
          this.updateTrack();
        }
      };

      var onEnd = (e) => {
        if (!this._touchActive) return;
        this._touchActive = false;
        if (!this.isMobile()) return;

        var touch = e.changedTouches[0];
        var dx = touch.clientX - this._touchStartX;
        var dy = touch.clientY - this._touchStartY;
        this.classList.remove('is-dragging');
        this._dragOffset = 0;

        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 48) {
          if (dx < 0) this.next();
          else this.go(this.index - 1);
          this.restartAutoplay();
        } else {
          this.updateTrack();
          this.resume();
        }
      };

      this.querySelectorAll('.mt-banner__media').forEach((el) => {
        el.addEventListener('touchstart', onStart, { passive: true });
        el.addEventListener('touchmove', onMove, { passive: false });
        el.addEventListener('touchend', onEnd, { passive: true });
        el.addEventListener('touchcancel', onEnd, { passive: true });
      });
    }

    go(i) {
      if (!this.slides.length) return;
      var len = this.slides.length;
      this.index = ((i % len) + len) % len;
      this._dragOffset = 0;
      this.classList.remove('is-dragging');
      this.slides.forEach((slide, n) => {
        var on = n === this.index;
        slide.classList.toggle('is-active', on);
        if (on) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach((dot) => {
        var dotIndex = parseInt(dot.getAttribute('data-index'), 10) || 0;
        var on = dotIndex === this.index;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.updateTrack();
      this.syncSlideVideos();
    }

    next() {
      this.go(this.index + 1);
    }

    startAutoplay() {
      this.pause();
      if (!this.autoplay || this.reduceMotion) return;
      this.timerId = setInterval(() => this.next(), this.interval);
    }

    restartAutoplay() {
      if (!this.autoplay || this.reduceMotion) return;
      this.startAutoplay();
    }

    pause() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    }

    resume() {
      this.startAutoplay();
    }
  }

  if (!customElements.get('mt-banner')) customElements.define('mt-banner', MtBanner);

  function bindDirectionFill(buttons, fillSelector) {
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    buttons.forEach((btn) => {
      var fill = btn.querySelector(fillSelector);
      if (!fill || btn.disabled) return;

      var side = (e) => {
        var rect = btn.getBoundingClientRect();
        return e.clientX < rect.left + rect.width / 2 ? 'left' : 'right';
      };

      btn.addEventListener('pointerenter', (e) => {
        if (reduceMotion) {
          btn.classList.add('is-fill');
          return;
        }
        fill.style.transition = 'none';
        fill.style.transformOrigin = side(e);
        void fill.offsetWidth;
        fill.style.transition = 'transform 0.38s cubic-bezier(0.4, 0, 0.2, 1)';
        fill.style.transform = 'scaleX(1)';
        btn.classList.add('is-fill');
      });

      btn.addEventListener('pointerleave', (e) => {
        if (reduceMotion) {
          btn.classList.remove('is-fill');
          return;
        }
        fill.style.transformOrigin = side(e);
        fill.style.transform = 'scaleX(0)';
        btn.classList.remove('is-fill');
      });
    });
  }

  function bindLineupButtonFill(buttons, fillSelector, signal) {
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var desktopMq = window.matchMedia('(min-width: 990px)');
    var opts = signal ? { signal: signal } : undefined;
    var hiddenLeft = 'inset(0 100% 0 0 round 999px)';
    var hiddenRight = 'inset(0 0 0 100% round 999px)';
    var shown = 'inset(0 0 0 0 round 999px)';

    buttons.forEach(function (btn) {
      var fill = btn.querySelector(fillSelector);
      if (!fill) return;

      var hiddenFor = function (e) {
        var rect = btn.getBoundingClientRect();
        return e.clientX < rect.left + rect.width / 2 ? hiddenLeft : hiddenRight;
      };

      var unsettle = function () {
        btn.classList.remove('is-fill-settled');
      };

      var settle = function () {
        btn.classList.add('is-fill-settled');
      };

      fill.addEventListener('transitionend', function (e) {
        if (e.propertyName !== 'clip-path' || !desktopMq.matches) return;
        if (btn.classList.contains('is-fill')) settle();
        else unsettle();
      }, opts);

      btn.addEventListener('pointerenter', function (e) {
        if (!desktopMq.matches) return;
        unsettle();
        if (reduceMotion) {
          fill.style.clipPath = shown;
          btn.classList.add('is-fill');
          settle();
          return;
        }
        fill.style.transition = 'none';
        fill.style.clipPath = hiddenFor(e);
        void fill.offsetWidth;
        fill.style.transition = 'clip-path 0.38s cubic-bezier(0.4, 0, 0.2, 1)';
        fill.style.clipPath = shown;
        btn.classList.add('is-fill');
      }, opts);

      btn.addEventListener('pointerleave', function (e) {
        if (!desktopMq.matches) return;
        unsettle();
        if (reduceMotion) {
          fill.style.clipPath = hiddenLeft;
          btn.classList.remove('is-fill');
          return;
        }
        fill.style.transition = 'clip-path 0.38s cubic-bezier(0.4, 0, 0.2, 1)';
        fill.style.clipPath = hiddenFor(e);
        btn.classList.remove('is-fill');
      }, opts);
    });
  }

  class MtLineup extends HTMLElement {
    connectedCallback() {
      this._abort = new AbortController();
      var signal = this._abort.signal;
      this.panels = this.querySelector('[data-lineup-panels]');
      this.mq = window.matchMedia('(min-width: 990px)');
      this._hoverIndex = -1;
      this._tabIndex = 0;

      this.refreshNodes();
      this.tabPill = this.querySelector('.mt-lineup__tab-pill');

      if (this.panels) {
        this.panels.addEventListener('mouseover', (e) => this.onTrackOver(e), { signal });
        this.panels.addEventListener('mouseleave', () => this.onTrackLeave(), { signal });
        this.panels.addEventListener('focusin', (e) => this.onTrackFocus(e), { signal });
      }

      this.addEventListener('click', (e) => this.onClick(e), { signal });

      if (this.tracks.length) {
        this.activateTab(0);
      } else {
        this.updateTabPill();
      }

      var tabsWrap = this.querySelector('.mt-lineup__tabs');
      if (tabsWrap && typeof ResizeObserver !== 'undefined') {
        this._tabRo = new ResizeObserver(() => this.updateTabPill());
        this._tabRo.observe(tabsWrap);
      }

      bindLineupButtonFill(this.querySelectorAll('.mt-lineup__btn'), '.mt-lineup__btn-fill', signal);
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this._tabRo) this._tabRo.disconnect();
    }

    refreshNodes() {
      this.tracks = Array.from(this.querySelectorAll('[data-lineup-track]'));
      this.tabs = Array.from(this.querySelectorAll('[data-lineup-tab]'));
    }

    getActiveTrack() {
      return this.tracks.find((track) => !track.hasAttribute('hidden')) || this.tracks[0] || null;
    }

    getTrackCards(track) {
      return Array.from(track.querySelectorAll('[data-lineup-card]'));
    }

    onClick(e) {
      var tab = e.target.closest('[data-lineup-tab]');
      if (!tab || !this.contains(tab)) return;
      var i = parseInt(tab.getAttribute('data-index'), 10) || 0;
      this.activateTab(i);
    }

    onTrackOver(e) {
      if (!this.mq.matches) return;
      var card = e.target.closest('[data-lineup-card]');
      var track = e.target.closest('[data-lineup-track]');
      if (!card || !track || track.hasAttribute('hidden')) return;
      var index = parseInt(card.getAttribute('data-index'), 10) || 0;
      if (index === this._hoverIndex && track === this.getActiveTrack()) return;
      this._hoverIndex = index;
      this.activateCard(track, index);
      track.classList.add('has-hover');
    }

    onTrackLeave() {
      if (!this.mq.matches) return;
      this._hoverIndex = -1;
      var track = this.getActiveTrack();
      if (track) {
        track.classList.remove('has-hover');
        this.getTrackCards(track).forEach((card) => card.classList.remove('is-active'));
      }
    }

    onTrackFocus(e) {
      if (!this.mq.matches) return;
      var card = e.target.closest('[data-lineup-card]');
      var track = e.target.closest('[data-lineup-track]');
      if (!card || !track || track.hasAttribute('hidden')) return;
      this.activateCard(track, parseInt(card.getAttribute('data-index'), 10) || 0);
      track.classList.add('has-hover');
    }

    activateTab(index) {
      this.refreshNodes();
      if (!this.tracks.length) return;

      var len = this.tracks.length;
      this._tabIndex = ((index % len) + len) % len;
      this._hoverIndex = -1;

      this.tracks.forEach((track, n) => {
        var on = n === this._tabIndex;
        track.classList.toggle('is-active', on);
        if (on) track.removeAttribute('hidden');
        else track.setAttribute('hidden', '');
        track.classList.remove('has-hover');
        this.getTrackCards(track).forEach((card) => card.classList.remove('is-active'));
      });

      this.tabs.forEach((tab) => {
        var i = parseInt(tab.getAttribute('data-index'), 10) || 0;
        var on = i === this._tabIndex;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      this.updateTabPill();
    }

    updateTabPill() {
      if (!this.tabPill || !this.tabs.length) return;
      var active = this.tabs[this._tabIndex];
      if (!active) return;
      var wrap = this.tabPill.parentElement;
      if (!wrap) return;

      var move = () => {
        var wrapRect = wrap.getBoundingClientRect();
        var rect = active.getBoundingClientRect();
        this.tabPill.style.width = rect.width + 'px';
        this.tabPill.style.transform = 'translateX(' + (rect.left - wrapRect.left) + 'px)';
      };

      move();
      requestAnimationFrame(move);
    }

    activateCard(track, index) {
      if (!track) return;
      this.getTrackCards(track).forEach((card, n) => {
        card.classList.toggle('is-active', n === index);
      });
    }
  }

  if (!customElements.get('mt-lineup')) customElements.define('mt-lineup', MtLineup);
  class MtStory extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.slides = Array.from(this.querySelectorAll('[data-story-slide]'));
      this.tabs = Array.from(this.querySelectorAll('[data-story-tab]'));
      this.index = 0;
      this.timerId = null;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.interval = Math.max(3000, parseInt(this.dataset.interval, 10) || 7000);
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._inView = true;
      this._desktopMq = window.matchMedia('(min-width: 750px)');

      this._onVisibility = () => this.syncAutoplay();
      document.addEventListener('visibilitychange', this._onVisibility);

      this._onViewportChange = () => {
        this.pauseAllVideos();
        this.loadActiveVideo();
      };
      if (this._desktopMq.addEventListener) {
        this._desktopMq.addEventListener('change', this._onViewportChange);
      } else if (this._desktopMq.addListener) {
        this._desktopMq.addListener(this._onViewportChange);
      }

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          this.go(parseInt(tab.getAttribute('data-index'), 10) || 0);
          this.syncAutoplay();
        });
      });

      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this._inView = entry.isIntersecting;
            if (entry.isIntersecting) this.loadActiveVideo();
            else this.pauseAllVideos();
            this.syncAutoplay();
          });
        },
        { threshold: 0.1, rootMargin: '40px 0px' }
      );
      this._io.observe(this);

      this.loadActiveVideo();
      this.syncAutoplay();
    }

    disconnectedCallback() {
      this.clearTimer();
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (this._desktopMq) {
        if (this._desktopMq.removeEventListener) {
          this._desktopMq.removeEventListener('change', this._onViewportChange);
        } else if (this._desktopMq.removeListener) {
          this._desktopMq.removeListener(this._onViewportChange);
        }
      }
      if (this._io) this._io.disconnect();
    }

    isDesktopViewport() {
      return this._desktopMq ? this._desktopMq.matches : window.innerWidth >= 750;
    }

    getSlideVideo(slide) {
      if (!slide) return null;
      var scope = this.isDesktopViewport() ? '.mt-story__media-desk' : '.mt-story__media-mob';
      return slide.querySelector(scope + ' [data-story-video]');
    }

    shouldAutoplay() {
      return this.autoplay && !this.reduceMotion && this._inView && !document.hidden;
    }

    syncAutoplay() {
      this.clearTimer();
      if (this.shouldAutoplay()) {
        this.classList.add('is-autoplay');
        this.restartProgressAnimation();
        this.timerId = setInterval(() => {
          if (!this.shouldAutoplay()) {
            this.syncAutoplay();
            return;
          }
          this.next();
        }, this.interval);
      } else {
        this.classList.remove('is-autoplay');
      }
    }

    clearTimer() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    }

    restartProgressAnimation() {
      var tab = this.tabs[this.index];
      if (!tab) return;
      var fill = tab.querySelector('.mt-story__progress-fill');
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.animation = '';
    }

    go(i) {
      if (!this.slides.length) return;
      var len = this.slides.length;
      this.index = ((i % len) + len) % len;

      this.slides.forEach((slide, n) => {
        var on = n === this.index;
        slide.classList.toggle('is-active', on);
        slide.classList.remove('is-playing');
        if (on) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });

      this.tabs.forEach((tab, n) => {
        var on = n === this.index;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        var fill = tab.querySelector('.mt-story__progress-fill');
        if (fill) {
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        }
      });

      this.pauseAllVideos();
      this.loadActiveVideo();
      if (this.shouldAutoplay()) this.restartProgressAnimation();
    }

    next() {
      this.go(this.index + 1);
    }

    loadActiveVideo() {
      var slide = this.slides[this.index];
      if (!slide) return;
      var video = this.getSlideVideo(slide);
      if (!video) return;

      this.hydrateVideo(video);
      if (this.slides.length > 1) {
        this.preloadSlideVideo(this.slides[(this.index + 1) % this.slides.length]);
      }
      this.playSlideVideo(slide, video);
    }

    hydrateVideo(video) {
      if (!video || video.dataset.hydrated === 'true') return;
      var hasLazy = false;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        hasLazy = true;
        if (!source.getAttribute('src')) {
          source.setAttribute('src', source.getAttribute('data-src'));
        }
      });
      video.dataset.hydrated = 'true';
      if (hasLazy && video.readyState === 0) video.load();
    }

    preloadSlideVideo(slide) {
      if (!slide) return;
      var video = this.getSlideVideo(slide);
      if (video) this.hydrateVideo(video);
    }

    playSlideVideo(slide, video) {
      if (!this._inView) return;

      if (video._storyCanplay) {
        video.removeEventListener('canplay', video._storyCanplay);
        video._storyCanplay = null;
      }

      var markPlaying = () => slide.classList.add('is-playing');
      var tryPlay = () => {
        var p = video.play();
        if (p && p.then) {
          p.then(markPlaying).catch(() => {});
        } else {
          markPlaying();
        }
      };

      if (video.readyState >= 3) {
        try {
          video.currentTime = 0;
        } catch (e) {}
        tryPlay();
        return;
      }

      var onReady = () => {
        video.removeEventListener('canplay', onReady);
        video._storyCanplay = null;
        try {
          video.currentTime = 0;
        } catch (e) {}
        tryPlay();
      };
      video._storyCanplay = onReady;
      video.addEventListener('canplay', onReady);
    }

    pauseAllVideos() {
      this.slides.forEach((slide) => {
        slide.querySelectorAll('[data-story-video]').forEach((video) => {
          if (video._storyCanplay) {
            video.removeEventListener('canplay', video._storyCanplay);
            video._storyCanplay = null;
          }
          video.pause();
        });
        slide.classList.remove('is-playing');
      });
    }
  }

  if (!customElements.get('mt-story')) customElements.define('mt-story', MtStory);
  class MtHotspots extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.panel = this.querySelector('[data-hotspot-panel]');
      this.scrim = this.querySelector('[data-hotspot-scrim]');
      this.dots = Array.from(this.querySelectorAll('[data-hotspot-open]'));
      this.details = Array.from(this.querySelectorAll('[data-hotspot-detail]'));
      this.pagers = Array.from(this.querySelectorAll('[data-hotspot-pager]'));
      this.index = 0;
      this.count = this.details.length;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._lastFocus = null;

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          var i = parseInt(dot.getAttribute('data-index'), 10) || 0;
          if (this.classList.contains('is-open') && this.index === i) this.close();
          else this.open(i);
        });
      });

      var closeBtn = this.querySelector('[data-hotspot-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());
      if (this.scrim) this.scrim.addEventListener('click', () => this.close());

      this.querySelectorAll('[data-hotspot-prev]').forEach((btn) => {
        btn.addEventListener('click', () => this.open(this.index - 1));
      });
      this.querySelectorAll('[data-hotspot-next]').forEach((btn) => {
        btn.addEventListener('click', () => this.open(this.index + 1));
      });

      this._onKey = (e) => {
        if (!this.classList.contains('is-open')) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowLeft') this.open(this.index - 1);
        if (e.key === 'ArrowRight') this.open(this.index + 1);
      };
      document.addEventListener('keydown', this._onKey);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKey);
      document.body.style.overflow = '';
    }

    pad(n) {
      return n < 10 ? '0' + n : String(n);
    }

    open(i) {
      if (!this.count) return;
      this.index = ((i % this.count) + this.count) % this.count;
      this._lastFocus = document.activeElement;

      this.details.forEach((el, n) => {
        if (n === this.index) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      });

      this.dots.forEach((dot, n) => {
        var on = n === this.index;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-expanded', on ? 'true' : 'false');
      });

      if (this.pagers.length) {
        var current = this.pad(this.index + 1);
        var total = ' / ' + this.pad(this.count);
        this.querySelectorAll('[data-hotspot-pager-current]').forEach((el) => {
          el.textContent = current;
        });
        this.querySelectorAll('[data-hotspot-pager-total]').forEach((el) => {
          el.textContent = total;
        });
      }

      if (this.panel) {
        this.panel.removeAttribute('hidden');
        this.panel.setAttribute('aria-hidden', 'false');
      }
      if (this.scrim) this.scrim.removeAttribute('hidden');
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      var closeBtn = this.querySelector('[data-hotspot-close]');
      if (closeBtn) closeBtn.focus();
    }

    close() {
      this.classList.remove('is-open');
      this.dots.forEach((dot) => {
        dot.classList.remove('is-active');
        dot.setAttribute('aria-expanded', 'false');
      });
      if (this.panel) {
        this.panel.setAttribute('aria-hidden', 'true');
        var delay = this.reduceMotion ? 0 : 320;
        setTimeout(() => {
          if (!this.classList.contains('is-open') && this.panel) {
            this.panel.setAttribute('hidden', '');
          }
        }, delay);
      }
      if (this.scrim) {
        setTimeout(() => {
          if (!this.classList.contains('is-open') && this.scrim) {
            this.scrim.setAttribute('hidden', '');
          }
        }, this.reduceMotion ? 0 : 300);
      }
      document.body.style.overflow = '';
      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
    }
  }

  if (!customElements.get('mt-hotspots')) customElements.define('mt-hotspots', MtHotspots);
  class MtPress extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var autoplay = this.dataset.autoplay !== 'false';
      if (autoplay && !this.reduceMotion) this.classList.add('is-autoplay');
    }
  }

  if (!customElements.get('mt-press')) customElements.define('mt-press', MtPress);
  class MtCommunity extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.track = this.querySelector('[data-community-track]');
      this.prev = this.querySelector('[data-community-prev]');
      this.next = this.querySelector('[data-community-next]');

      if (this.prev) this.prev.addEventListener('click', () => this.scrollBy(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollBy(1));

      this.querySelectorAll('[data-community-atc]').forEach((btn) => {
        btn.addEventListener('click', () => this.addToCart(btn));
      });

      this.initAtcDirectionFill();
      this.initNavDirectionFill();

      this._videoIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            var slide = entry.target.closest('.mt-community__slide');
            var video = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
              this.loadVideo(video);
              var p = video.play();
              if (p && p.then) {
                p.then(() => {
                  if (slide) slide.classList.add('is-playing');
                }).catch(() => {});
              } else if (slide) {
                slide.classList.add('is-playing');
              }
            } else {
              video.pause();
              if (slide) slide.classList.remove('is-playing');
            }
          });
        },
        { threshold: [0, 0.55, 1], root: this.track }
      );

      this.querySelectorAll('[data-community-video]').forEach((video) => {
        this._videoIo.observe(video);
      });
    }

    disconnectedCallback() {
      if (this._videoIo) this._videoIo.disconnect();
    }

    slideWidth() {
      var slide = this.track && this.track.querySelector('.mt-community__slide');
      if (!slide) return 280;
      var styles = getComputedStyle(this.track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    scrollBy(dir) {
      if (!this.track) return;
      this.track.scrollBy({ left: dir * this.slideWidth(), behavior: 'smooth' });
    }

    initAtcDirectionFill() {
      bindDirectionFill(
        this.querySelectorAll('[data-community-atc]:not(:disabled)'),
        '.mt-community__atc-fill'
      );
    }

    initNavDirectionFill() {
      bindDirectionFill(
        this.querySelectorAll('.mt-community__nav-btn'),
        '.mt-community__nav-btn-fill'
      );
    }

    loadVideo(video) {
      video.querySelectorAll('source[data-src]').forEach((source) => {
        if (!source.getAttribute('src')) {
          source.setAttribute('src', source.getAttribute('data-src'));
        }
      });
      if (video.readyState < 2) video.load();
    }

    async addToCart(btn) {
      var id = btn.getAttribute('data-variant-id');
      if (!id || btn.disabled) return;
      btn.classList.add('is-loading');
      try {
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ id: id, quantity: 1 })
        });
        if (!res.ok) throw new Error('add failed');
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'product' } }));
      } catch (e) {
      } finally {
        btn.classList.remove('is-loading');
      }
    }
  }

  if (!customElements.get('mt-community')) customElements.define('mt-community', MtCommunity);
  class MtLoved extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.dataset.autoplay !== 'false' && !this.reduceMotion) {
        this.classList.add('is-autoplay');
      }

      this.mobileList = this.querySelector('[data-loved-mobile]');
      this.mobileMore = this.querySelector('[data-loved-mobile-more]');
      this.mobileVisible = 2;
      this.mobileStep = 3;

      if (this.mobileList && this.mobileMore) {
        this.updateMobileCards();
        this.mobileMore.addEventListener('click', () => {
          this.mobileVisible += this.mobileStep;
          this.updateMobileCards();
        });
      }

      this.querySelectorAll('[data-loved-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.dismissCard(btn));
      });

      this.querySelectorAll('[data-loved-play]').forEach((btn) => {
        btn.addEventListener('click', () => this.toggleVideo(btn));
      });

      this._videoIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            var item = entry.target.closest('[data-loved-item]');
            var video = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio > 0.35) {
              this.loadVideo(video);
            } else {
              video.pause();
              if (item) item.classList.remove('is-playing');
            }
          });
        },
        { threshold: [0, 0.35, 0.6] }
      );

      this.querySelectorAll('[data-loved-video]').forEach((video) => {
        this._videoIo.observe(video);
      });
    }

    disconnectedCallback() {
      if (this._videoIo) this._videoIo.disconnect();
    }

    dismissCard(btn) {
      var item = btn.closest('[data-loved-item]');
      if (!item) return;
      var id = item.getAttribute('data-loved-item');
      this.querySelectorAll('[data-loved-item="' + id + '"]').forEach((el) => {
        el.classList.add('is-dismissed');
      });
      this.updateMobileCards();
    }

    updateMobileCards() {
      if (!this.mobileList) return;

      var items = Array.from(this.mobileList.querySelectorAll('.mt-loved__item:not(.is-dismissed)'));
      items.forEach((item, index) => {
        item.classList.toggle('is-mobile-hidden', index >= this.mobileVisible);
      });

      if (this.mobileMore) {
        this.mobileMore.hidden = items.length <= this.mobileVisible;
      }
    }

    loadVideo(video) {
      video.querySelectorAll('source[data-src]').forEach((source) => {
        if (!source.src) source.src = source.getAttribute('data-src');
      });
      if (video.readyState < 1) video.load();
    }

    toggleVideo(btn) {
      var item = btn.closest('[data-loved-item]');
      var video = item && item.querySelector('[data-loved-video]');
      if (!video) return;
      this.loadVideo(video);
      if (video.paused) {
        var p = video.play();
        if (p && p.then) {
          p.then(() => item.classList.add('is-playing')).catch(() => {});
        } else {
          item.classList.add('is-playing');
        }
      } else {
        video.pause();
        item.classList.remove('is-playing');
      }
    }
  }

  if (!customElements.get('mt-loved')) {
    customElements.define('mt-loved', MtLoved);
  }

  /* from sections/mt-shot-video.liquid */
  if (!customElements.get('mt-shot-video')) {
    customElements.define('mt-shot-video', class extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('[data-shot-video]');
        if (!this.video) return;

        this.video.muted = true;
        this.video.playsInline = true;
        this.video.loop = true;
        this.video.controls = false;
        this.video.setAttribute('controlsList', 'nodownload noplaybackrate noremoteplayback');
        this.video.disablePictureInPicture = true;

        this._io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
                var playPromise = this.video.play();
                if (playPromise && playPromise.catch) {
                  playPromise.catch(function () {});
                }
              } else {
                this.video.pause();
              }
            });
          },
          { threshold: [0, 0.35, 0.6] }
        );
        this._io.observe(this.video);
      }

      disconnectedCallback() {
        if (this._io) this._io.disconnect();
      }
    });
  }
